import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/hooks/useConfirm';
import { unsentCount } from '@/services/pendingQueue';

/**
 * Sign out, asking first when this person has work on the device that has not
 * reached the server (FM Technician A16, decision D8).
 *
 * The unsent items are KEPT — the queue is per person, so they wait for this
 * same person to sign in again and are never sent under anyone else's login.
 * What the question protects is the person who thinks it all went: a
 * technician handing the phone to the next shift with a reading still on it.
 * The read cache goes with the sign-out (useAuth's logout clears the query
 * cache), so the next person never sees this one's jobs.
 *
 *   const { signOut, signOutDialog } = useSignOut();
 *   <button onClick={signOut}>Sign out</button>
 *   {signOutDialog}
 *
 * signOut() resolves to whether the sign-out really happened: false when the
 * person chose "Stay signed in". `{ ask: false }` skips the question — the PIN
 * lock uses it (FM Technician review R2-1): the lock screen already says the
 * saved work stays, and a "Stay signed in" there would have to leave the lock
 * up anyway, so asking would only be a way to an unlocked app.
 */
export function useSignOut() {
  const { logout } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const signOut = async ({ ask = true }: { ask?: boolean } = {}): Promise<boolean> => {
    const n = unsentCount();
    if (ask && n > 0) {
      const ok = await confirm({
        title: `${n} ${n === 1 ? 'item has' : 'items have'} not been sent`,
        message:
          `${n === 1 ? 'It stays' : 'They stay'} saved on this device for you, but ` +
          `${n === 1 ? 'it' : 'they'} will not be sent until you sign in here again. ` +
          'Open Unsent work to send now, or sign out anyway.',
        confirmLabel: 'Sign out anyway',
        cancelLabel: 'Stay signed in',
        variant: 'warning',
      });
      if (!ok) return false;
    }
    logout();
    return true;
  };
  return { signOut, signOutDialog: <>{confirmDialog}</> };
}
